import { useContext, useEffect, useState } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { AuthContext } from "../../context/authcontext";
import {
    isPushNotificationSupported,
    registerPushSubscription,
    requestNotificationPermission,
} from "../../utils/pushNotificationUtils";
import { isIos, isMobileOrTabletDevice } from "../../utils/deviceDetection";

const MySwal = withReactContent(Swal);

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const isStandalone = () =>
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true;

export default function PwaInstallBanner() {
    const { auth, selectedTenantId } = useContext(AuthContext);

    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [installed, setInstalled] = useState(
        isStandalone() || localStorage.getItem("pwa_installed") === "true"
    );
    const [permission, setPermission] = useState<NotificationPermission | "unsupported">(
        "Notification" in window ? Notification.permission : "unsupported"
    );
    const [dismissed, setDismissed] = useState(false);
    const [working, setWorking] = useState(false);
    const [isInstalling, setIsInstalling] = useState(false);
    const [installProgress, setInstallProgress] = useState(0);
    const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);

    useEffect(() => {
        const handleBeforeInstallPrompt = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e as BeforeInstallPromptEvent);
            setInstalled(false);
            localStorage.removeItem("pwa_installed");
        };
        const handleAppInstalled = () => {
            setInstalled(true);
            // We intentionally do not set isInstalling(false) here so the 7-sec progress bar can finish smoothly.
            setDeferredPrompt(null);
            localStorage.setItem("pwa_installed", "true");
        };

        window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
        window.addEventListener("appinstalled", handleAppInstalled);

        return () => {
            window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
            window.removeEventListener("appinstalled", handleAppInstalled);
        };
    }, []);

    useEffect(() => {
        if ("serviceWorker" in navigator && "PushManager" in window) {
            navigator.serviceWorker.getRegistration().then((reg) => {
                if (reg) {
                    reg.pushManager.getSubscription().then((sub) => {
                        setHasSubscription(!!sub);
                    }).catch(() => {
                        setHasSubscription(false);
                    });
                } else {
                    setHasSubscription(false);
                }
            }).catch(() => {
                setHasSubscription(false);
            });
        } else {
            setHasSubscription(false);
        }
    }, []);

    const isStandaloneMode = isStandalone();
    const notificationsSupported = isPushNotificationSupported();

    // Only show install banner if we can actually install it programmatically (or on iOS)
    const canInstall = !!deferredPrompt || isIos();
    const needsInstall = !installed && canInstall;

    // It's only fully enabled if permission is granted AND they have an active subscription
    const notificationsEnabled = permission === "granted" && hasSubscription !== false;

    const needsNotifications = installed && notificationsSupported && !notificationsEnabled;

    const shouldShow =
        !!auth?.user_id &&
        isMobileOrTabletDevice() &&
        !dismissed &&
        (needsInstall || needsNotifications || isInstalling);

    const handleInstallAndEnable = async () => {
        if (working) return;
        setWorking(true);

        try {
            if (needsInstall) {
                if (deferredPrompt) {
                    try {
                        await deferredPrompt.prompt();
                        const { outcome } = await deferredPrompt.userChoice;

                        if (outcome === "accepted") {
                            setIsInstalling(true);
                            setInstallProgress(0);

                            const duration = 10000;
                            const interval = 100;
                            const steps = duration / interval;
                            let currentStep = 0;

                            const timer = setInterval(() => {
                                currentStep++;
                                setInstallProgress(Math.min(100, Math.floor((currentStep / steps) * 100)));

                                if (currentStep >= steps) {
                                    clearInterval(timer);
                                    setIsInstalling(false);
                                    setInstalled(true);
                                    localStorage.setItem("pwa_installed", "true");
                                    setDeferredPrompt(null);
                                }
                            }, interval);
                        }
                    } catch (installError) {
                        // Prompt can reject if activation already expired;
                    }

                    return; // prevent finally block from setting working=false early
                } else if (isIos()) {
                    window.alert(
                        'To install Cloudee CRM: tap the Share icon in Safari, then "Add to Home Screen".'
                    );
                } else {
                    window.alert(
                        'To install Cloudee CRM: tap the browser menu (⋮), then "Install app" or "Add to Home screen".'
                    );
                }
            } else if (needsNotifications) {
                const newPermission = await requestNotificationPermission();
                setPermission(newPermission);

                if (newPermission === "granted") {
                    const tenantId = Number(selectedTenantId);
                    const userId = Number(auth?.user_id);
                    if (tenantId && userId) {
                        const registered = await registerPushSubscription(tenantId, userId);
                        if (registered) {
                            setHasSubscription(true);
                            MySwal.fire({
                                toast: true,
                                position: "top-end",
                                icon: "success",
                                title: "Notification subscribed",
                                showConfirmButton: false,
                                timer: 3000,
                                timerProgressBar: true,
                            });
                        } else {
                            MySwal.fire({
                                icon: "error",
                                title: "Subscription Failed",
                                text: "Could not register push subscription on the device. Please check app settings.",
                            });
                        }
                    } else {
                        MySwal.fire({
                            icon: "error",
                            title: "Missing Information",
                            text: "Please select a tenant and ensure you are logged in.",
                        });
                    }
                }
            }
        } finally {
            setWorking(false);
        }
    };

    if (!shouldShow) return null;

    let message = "";
    let buttonLabel = "";
    let canTakeAction = false;
    let buttonDisabled = working || isInstalling;

    if (isInstalling) {
        message = "Installing Cloudee CRM to your device... please wait.";
        buttonLabel = "Enable Notifications";
        canTakeAction = true;
    } else if (needsInstall) {
        message = "Install Cloudee CRM — you'll be asked to enable notifications right after.";
        buttonLabel = "Install App";
        canTakeAction = true;
    } else if (needsNotifications) {
        if (!isStandaloneMode) {
            message = "Please open the Cloudee CRM app from your home screen to enable notifications.";
            buttonLabel = "";
            canTakeAction = false;
        } else {
            message = "Enable notifications so you don't miss a new chat.";
            buttonLabel = "Enable Notifications";
            canTakeAction = true;
        }
    }

    return (
        <div
            style={{
                position: "fixed",
                left: "16px",
                right: "16px",
                bottom: "16px",
                zIndex: 10002,
                display: "flex",
                justifyContent: "center",
                pointerEvents: "none",
            }}
        >
            <div
                style={{
                    pointerEvents: "auto",
                    width: "100%",
                    maxWidth: "850px",
                    background: "#ffffff",
                    borderRadius: "14px",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.08)",
                    border: "1px solid rgba(0,0,0,0.08)",
                    padding: "16px 20px",
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: "14px",
                    position: "relative",
                }}
            >
                <div
                    style={{
                        width: "44px",
                        height: "44px",
                        borderRadius: "12px",
                        background: "#00918e",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    <i className="ti ti-bell-ringing" style={{ fontSize: "22px", color: "white" }} />
                </div>

                <div style={{ flex: "1 1 240px", minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: "14.5px", fontWeight: 600, color: "#2d3748" }}>
                        Stay on top of every chat
                    </p>
                    <p style={{ margin: "2px 0 0", fontSize: "10px", color: "#6b7280" }}>
                        {message}
                    </p>
                    {isInstalling && (
                        <div style={{ marginTop: "12px", display: "flex", alignItems: "center", gap: "12px" }}>
                            <div style={{ flex: 1, background: "#e2e8f0", height: "8px", borderRadius: "4px", overflow: "hidden" }}>
                                <div style={{ width: `${installProgress}%`, background: "#00918e", height: "100%", transition: "width 0.1s linear" }} />
                            </div>
                            <span style={{ fontSize: "13px", fontWeight: 600, color: "#2d3748", minWidth: "35px" }}>
                                {installProgress}%
                            </span>
                        </div>
                    )}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
                    {canTakeAction && (
                        <button
                            onClick={handleInstallAndEnable}
                            disabled={buttonDisabled}
                            style={{
                                background: "#00918e",
                                color: "white",
                                border: "none",
                                borderRadius: "8px",
                                padding: "9px 18px",
                                fontSize: "14px",
                                fontWeight: 600,
                                cursor: buttonDisabled ? "not-allowed" : "pointer",
                                opacity: buttonDisabled ? 0.5 : 1,
                                whiteSpace: "nowrap",
                            }}
                        >
                            {buttonLabel}
                        </button>
                    )}

                    <button
                        onClick={() => setDismissed(true)}
                        aria-label="Dismiss"
                        style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            color: "#a0aec0",
                            fontSize: "18px",
                            lineHeight: 1,
                            width: "28px",
                            height: "28px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "6px",
                            position: "absolute",
                            top: "8px",
                            right: "8px",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = "rgba(0,0,0,0.05)";
                            e.currentTarget.style.color = "#2d3748";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = "transparent";
                            e.currentTarget.style.color = "#a0aec0";
                        }}
                    >
                        ×
                    </button>
                </div>
            </div>
        </div>
    );
}
